
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import {
  BaseFilter,
  Align,
  SelectFilter,
  CheckboxFilter,
  Avatar,
  Size,
  Color,
  Flex,
  FlexItem,
  FlexJustify,
  Icon,
  Margin,
  Text,
  Tier,
} from '@drawbotics/react-drylus';
import Playground from '~/components/Playground';
import { ControlledField, ControlledMultiField } from '../utils';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<h1>{`Filter`}</h1>
<p>{`A filter uses a number of other UI elements. For example, we may use a date picker to filter by date, or a list of checkboxes to filter by a particular criterium. The component exposes the `}<inlineCode parentName="p">{`BaseFilter`}</inlineCode>{`, which is used to create custom filters, but also provides pre-built common components, see the examples below for more details.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { BaseFilter, SelectFilter, CheckboxFilter } from '@drawbotics/react-drylus';
`}</code></pre>
<p><strong parentName="p">{`Example`}</strong></p>
<Playground __position={0} __code={`<div style={{ height: 200, marginLeft: 100 }}>
  <BaseFilter label="Base filter" onClear={x=>x}>
    <div style={{ height: 70, background: 'red', padding: 20 }}>
      Some custom content
    </div>
  </BaseFilter>
</div>`} component={BaseFilter} mdxType="Playground">
  <div style={{
        height: 200,
        marginLeft: 100
      }}>
    <BaseFilter label="Base filter" onClear={x => x} mdxType="BaseFilter">
      <div style={{
            height: 70,
            background: 'red',
            padding: 20
          }}>
        Some custom content
      </div>
    </BaseFilter>
  </div>
</Playground>
<h4>{`Examples`}</h4>
<p><inlineCode parentName="p">{`SelectFilter`}</inlineCode>{` inherits all props from `}<inlineCode parentName="p">{`BaseFilter`}</inlineCode>{` and adds the following`}</p>
<Playground __position={1} __code={`<div style={{ height: 200 }}>
  <Flex justify={FlexJustify.END}>
    <FlexItem>
      <Margin size={{ right: Size.SMALL }}>
        <ControlledField component={
          <SelectFilter
            label="Country"
            onClear={x=>x}
            onChange={x=>x}
            options={[{
              leading: <Avatar text="Belgium" backgroundColor="#E00D2B" size={Size.SMALL} />,
              label: 'Belgium',
              value: 'BE',
            }, {
              leading: <Avatar text="France" backgroundColor="#011082" size={Size.SMALL} />,
              label: 'France',
              value: 'FR',
            }, {
              leading: <Avatar text="Italy" backgroundColor="#128435" size={Size.SMALL} />,
              label: 'Italy',
              value: 'IT',
            }]} />
        } />
      </Margin>
    </FlexItem>
    <FlexItem>
      <Margin size={{ right: Size.SMALL }}>
        <ControlledField component={
          <SelectFilter
            label="Notifications"
            onClear={x=>x}
            onChange={x=>x}
            options={[{
              leading: <Icon name="x" color={Color.RED} />,
              label: 'Dismissed',
              value: 'dismissed',
            }, {
              leading: <Icon name="bell" color={Color.ORANGE} />,
              label: 'Snoozed',
              value: 'snoozed',
            }, {
              leading: <Icon name="check" color={Color.GREEN} />,
              label: 'Seen',
              value: 'seen',
            }]} />
        } />
      </Margin>
    </FlexItem>
    <FlexItem>
      <ControlledField component={
        <SelectFilter
          label="Services"
          onClear={x=>x}
          onChange={x=>x}
          options={[{
            trailing: <Text tier={Tier.SECONDARY}>3</Text>,
            label: '3D Exterior',
            value: 'exterior',
          }, {
            trailing: <Text tier={Tier.SECONDARY}>1</Text>,
            label: '3D Interior',
            value: 'interior',
          }, {
            trailing: <Text tier={Tier.SECONDARY}>10</Text>,
            label: 'Shoebox',
            value: 'shoebox',
          }]} />
      } />
    </FlexItem>
  </Flex>
</div>`} component={SelectFilter} mdxType="Playground">
  <div style={{
        height: 200
      }}>
    <Flex justify={FlexJustify.END} mdxType="Flex">
      <FlexItem mdxType="FlexItem">
        <Margin size={{
              right: Size.SMALL
            }} mdxType="Margin">
          <ControlledField component={<SelectFilter label="Country" onClear={x => x} onChange={x => x} options={[{
                leading: <Avatar text="Belgium" backgroundColor="#E00D2B" size={Size.SMALL} mdxType="Avatar" />,
                label: 'Belgium',
                value: 'BE'
              }, {
                leading: <Avatar text="France" backgroundColor="#011082" size={Size.SMALL} mdxType="Avatar" />,
                label: 'France',
                value: 'FR'
              }, {
                leading: <Avatar text="Italy" backgroundColor="#128435" size={Size.SMALL} mdxType="Avatar" />,
                label: 'Italy',
                value: 'IT'
              }]} mdxType="SelectFilter" />} mdxType="ControlledField" />
        </Margin>
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Margin size={{
              right: Size.SMALL
            }} mdxType="Margin">
          <ControlledField component={<SelectFilter label="Notifications" onClear={x => x} onChange={x => x} options={[{
                leading: <Icon name="x" color={Color.RED} mdxType="Icon" />,
                label: 'Dismissed',
                value: 'dismissed'
              }, {
                leading: <Icon name="bell" color={Color.ORANGE} mdxType="Icon" />,
                label: 'Snoozed',
                value: 'snoozed'
              }, {
                leading: <Icon name="check" color={Color.GREEN} mdxType="Icon" />,
                label: 'Seen',
                value: 'seen'
              }]} mdxType="SelectFilter" />} mdxType="ControlledField" />
        </Margin>
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <ControlledField component={<SelectFilter label="Services" onClear={x => x} onChange={x => x} options={[{
              trailing: <Text tier={Tier.SECONDARY} mdxType="Text">3</Text>,
              label: '3D Exterior',
              value: 'exterior'
            }, {
              trailing: <Text tier={Tier.SECONDARY} mdxType="Text">1</Text>,
              label: '3D Interior',
              value: 'interior'
            }, {
              trailing: <Text tier={Tier.SECONDARY} mdxType="Text">10</Text>,
              label: 'Shoebox',
              value: 'shoebox'
            }]} mdxType="SelectFilter" />} mdxType="ControlledField" />
      </FlexItem>
    </Flex>
  </div>
</Playground>
<p><inlineCode parentName="p">{`CheckboxFilter`}</inlineCode>{` inherits all props from `}<inlineCode parentName="p">{`BaseFilter`}</inlineCode>{` and adds the following`}</p>
<Playground __position={2} __code={`<div style={{ height: 200, marginLeft: 100 }}>
  <ControlledMultiField component={
    <CheckboxFilter
      align={Align.LEFT}
      label="Products"
      onClear={x=>x}
      onChange={x=>x}
      options={[{
        label: 'Exterior 3D',
        value: 'exterior3d',
      }, {
        label: 'Interior 3D',
        value: 'interior3d',
      },{
        label: 'Separator',
        delimiter: true
      }, {
        label: 'Shoebox',
        value: 'shoebox',
      }, {
        label: 'Website',
        value: 'website',
      }]} />
  } />
</div>`} component={CheckboxFilter} mdxType="Playground">
  <div style={{
        height: 200,
        marginLeft: 100
      }}>
    <ControlledMultiField component={<CheckboxFilter align={Align.LEFT} label="Products" onClear={x => x} onChange={x => x} options={[{
          label: 'Exterior 3D',
          value: 'exterior3d'
        }, {
          label: 'Interior 3D',
          value: 'interior3d'
        }, {
          label: 'Separator',
          delimiter: true
        }, {
          label: 'Shoebox',
          value: 'shoebox'
        }, {
          label: 'Website',
          value: 'website'
        }]} mdxType="CheckboxFilter" />} mdxType="ControlledMultiField" />
  </div>
</Playground>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;